import { Link, useParams } from 'react-router-dom';
import SectionHeader from '@/components/feature/SectionHeader';
import DramaCard from '@/components/feature/DramaCard';
import FavoriteButton from './components/FavoriteButton';
import CommentSection from './components/CommentSection';
import { dramas, getDramaById } from '@/mocks/dramas';

export default function Detail() {
  const { id = '' } = useParams();
  const drama = getDramaById(id);

  if (!drama) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-content flex-col items-center justify-center px-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该剧集</h1>
        <p className="mt-2 text-sm text-foreground-500">该剧集可能已下线，或链接有误。</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link to="/" className="flex h-11 items-center whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50">
            返回首页
          </Link>
          <Link to="/category/guochan" className="flex h-11 items-center whitespace-nowrap rounded-md border border-background-300 px-6 text-sm text-foreground-700">
            浏览剧集分类
          </Link>
        </div>
      </div>
    );
  }

  const related = dramas
    .filter((d) => d.id !== drama.id)
    .map((d) => ({
      drama: d,
      score: (d.category === drama.category ? 2 : 0) + d.genres.filter((g) => drama.genres.includes(g)).length,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
    .map((x) => x.drama);

  const metaItems = [
    { label: '主演', value: drama.starring.join(' / ') },
    { label: '导演', value: drama.director },
    { label: '类型', value: drama.genres.join(' · ') },
    { label: '地区', value: drama.region },
    { label: '年份', value: `${drama.year}` },
    { label: '状态', value: drama.status === '连载中' ? `连载中 · 更新至 ${drama.updatedTo} 集` : '已完结' },
    { label: '集数', value: `${drama.episodes} 集` },
    { label: '播出时间', value: drama.updateInfo },
  ];

  return (
    <div className="w-full">
      {/* hero */}
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={drama.backdrop}
          alt={`${drama.title}主视觉剧照`}
          title={`${drama.title} - 详细剧情介绍与播出信息`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/40" />

        <div className="relative z-10 mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-300" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${drama.category}`} className="transition-colors hover:text-accent-400">
              {drama.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-400">{drama.title}</span>
          </nav>

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end">
            <div className="h-56 w-40 shrink-0 overflow-hidden rounded-lg border border-background-50/20 bg-background-200 md:h-64 md:w-44">
              <img
                src={drama.poster}
                alt={`${drama.title}剧照封面`}
                title={`${drama.title} - 最新电视剧推荐`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">{drama.categoryName}</span>
                <span className="rounded bg-background-50/15 px-2.5 py-1 text-xs text-background-50">{drama.year}</span>
                <span className={`rounded px-2.5 py-1 text-xs font-medium ${drama.status === '连载中' ? 'bg-accent-100 text-accent-800' : 'bg-secondary-200 text-secondary-900'}`}>
                  {drama.status}
                </span>
              </div>
              <h1 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">{drama.title}</h1>
              <p className="mt-2 text-sm text-accent-300">{drama.tagline}</p>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400" />
                  <strong className="font-heading text-lg text-background-50">{drama.rating.toFixed(1)}</strong>
                  <span className="text-xs text-background-400">评分</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-fill text-accent-400" />
                  {(drama.heat / 10000).toFixed(1)} 万热度
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" />
                  {drama.updateInfo}
                </span>
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <FavoriteButton dramaId={drama.id} title={drama.title} />
                <Link
                  to={`/category/${drama.category}`}
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 bg-background-50/10 px-5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20"
                >
                  <i className="ri-list-check-2" />
                  同分类剧集
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <section>
              <div className="mb-4 flex items-center gap-2">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
              </div>
              <p className="text-sm leading-7 text-foreground-700 md:text-base md:leading-8">{drama.synopsis}</p>
            </section>

            <section className="mt-10">
              <div className="mb-4 flex items-center gap-2">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl font-bold text-foreground-950">剧集信息</h2>
              </div>
              <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                {metaItems.map((m) => (
                  <div key={m.label} className="flex gap-3 border-b border-background-200 pb-3">
                    <dt className="w-20 shrink-0 text-sm text-foreground-500">{m.label}</dt>
                    <dd className="text-sm text-foreground-900">{m.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <CommentSection dramaId={drama.id} />
          </div>

          {/* sidebar */}
          <aside className="w-full shrink-0 lg:w-72">
            <div className="rounded-lg border border-background-200 bg-primary-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-calendar-schedule-line text-primary-600" />
                播出与更新
              </h3>
              <p className="mt-3 text-sm text-foreground-700">{drama.updateInfo}</p>
              <div className="mt-4 rounded-md bg-background-50 p-3">
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span>更新进度</span>
                  <span className="text-primary-600">
                    {drama.status === '连载中' ? `${drama.updatedTo} / ${drama.episodes} 集` : `${drama.episodes} / ${drama.episodes} 集`}
                  </span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                  <div
                    className="h-full rounded-full bg-accent-500"
                    style={{ width: `${Math.round((drama.updatedTo / drama.episodes) * 100)}%` }}
                  />
                </div>
              </div>
              <p className="mt-3 text-xs text-foreground-500">
                最近更新：{drama.updatedAt}
              </p>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-information-line text-primary-600" />
                观看提示
              </h3>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                本页面内容为演示用途，剧集资料与播出信息均为虚构示例，不提供播放地址。
              </p>
              <Link
                to="/about"
                className="mt-3 inline-flex items-center gap-1 text-xs text-primary-600 hover:text-accent-600"
              >
                了解网站说明
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <SectionHeader title="相关推荐" subtitle="根据分类与题材为你推荐" moreTo={`/category/${drama.category}`} />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {related.map((d) => (
              <DramaCard key={d.id} drama={d} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}